import * as React from "react";
import {
  Box,
  debounce,
  IconButton,
  inputLabelClasses,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import models from "../../constants/models";
import { app } from "../../services";
import { useTheme } from "@mui/styles";
import IconMask from "../IconMask";
import { useSelector } from "react-redux";
import CatalogsIconButton from "../customForms/CatalogsIconButton";
import { alpha } from "@material-ui/core";

const delaySearch = debounce((callback) => {
  callback();
}, 300);

export default function SearchSelect({
  control,
  id,
  field,
  defaultValue,
  modelBase,
  variant,
  ...props
}) {
  const theme = useTheme();
  const model =
    Object.values(models).find((e) => e.id === field?.relation?.id_model) ||
    Object.values(models).find(
      (e) => e.service === field?.relation?.vc_nameTable,
    ) ||
    {};

  const token = useSelector((state) => state.session.token);
  const user = useSelector((state) => state.session.globalUser);
  const institution = useSelector((state) => state.session.institution);

  const isStatic = Array.isArray(field?.options) && field.options.length > 0;
  const isReadOnly = !!field?.readOnly;

  // ⚠️ crear service con memo para que no cambie por render
  const service = React.useMemo(() => {
    if (isStatic) return null;
    if (!field?.relation?.vc_nameTable) return null;
    return app.service(field.relation.vc_nameTable);
  }, [isStatic, field?.relation?.vc_nameTable]);

  const [focus, setFocus] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);

  const isAdmin = institution?.roles?.find(
    (e) =>
      e.id_rol === "cab3b86a-09db-4808-85ff-bc235fd7b6f9" ||
      e.id_rol === "663536e2-bdb4-4887-a313-c42d3e00bc37",
  );

  const isSystem = user?.roles?.find(
    (e) => e.id_rol === "663536e2-bdb4-4887-a313-c42d3e00bc37",
  );

  // ✅ Cancelación / control de concurrencia
  const abortRef = React.useRef(null);
  const seqRef = React.useRef(0);

  React.useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const getLabel = (item) => {
    if (!item) return "";
    if (isStatic) return item.label ?? "";

    let label = "";
    (field?.relation?.fieldsDisplay || []).forEach((key, index) => {
      const part =
        (field?.relation?.objectPath
          ? (item[field?.relation?.objectPath] || {})[key]
          : item[key]) || "";
      label = `${label}${part}${field?.relation?.fieldsDisplaySeparators?.[index] || ""}`;
    });
    return label;
  };

  // ✅ llave estable para comparar opciones y value
  const getOptionKey = React.useCallback(
    (opt) => {
      if (!opt) return "";
      if (isStatic) return String(opt.value ?? "");
      // prioridad: id, si no: keyTable
      const key = opt.id ?? opt[field?.relation?.keyTable];
      return String(key ?? "");
    },
    [isStatic, field?.relation?.keyTable],
  );

  const getHiddenValue = () => {
    if (!value) return defaultValue || "";
    if (isStatic) return value.value ?? "";
    return value[field?.relation?.keyTable] ?? value.id ?? "";
  };

  const searchLocal = (searchText) => {
    const base = field?.options || [];
    if (!searchText) {
      setOptions(base);
      return;
    }
    const s = (searchText || "").toLowerCase();
    setOptions(
      base.filter((opt) =>
        String(opt.label ?? "")
          .toLowerCase()
          .includes(s),
      ),
    );
  };

  const buildAndFilters = () => {
    const filtersDefault = [];
    let validation = true;

    (model?.defaultSystem || []).forEach((item) => {
      switch (true) {
        case item.param === "institution":
          filtersDefault.push({ [item.vc_field]: institution?.[item.key] });
          break;
        case item.param === "user":
          filtersDefault.push({ [item.vc_field]: user?.[item.key] });
          break;
        case item.type === "static":
          filtersDefault.push({ [item.vc_field]: item.value });
          break;
        default:
          break;
      }
    });

    (field?.relation?.defaultSystem || []).forEach((item) => {
      switch (true) {
        case item.param === "institution":
          filtersDefault.push({ [item.vc_field]: institution?.[item.key] });
          break;
        case item.param === "user":
          filtersDefault.push({ [item.vc_field]: user?.[item.key] });
          break;
        case item.type === "static":
          filtersDefault.push({ [item.vc_field]: item.value });
          break;
        case item.type === "default":
          if (modelBase?.defaultValues && modelBase.defaultValues[item.key]) {
            filtersDefault.push({
              [item.vc_field]: modelBase.defaultValues[item.key],
            });
          } else if (item.required) validation = false;
          break;
        case item.type === "field": {
          const el = document.getElementById((model?.name || "") + item.key);
          console.log(el);
          if (
            el?.value !== undefined &&
            el?.value !== "" &&
            el?.value !== null
          ) {
            filtersDefault.push({ [item.vc_field]: el.value });
          } else if (item.required) validation = false;
          break;
        }
        default:
          break;
      }
    });

    const and = [...(model?.and || []), ...filtersDefault];
    return { and, validation };
  };

  const searchRemote = async (searchText) => {
    setOptions([]);

    if (!service) return;
    console.log("service", service);
    // ✅ cancelar request anterior
    if (abortRef.current) abortRef.current.abort();

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const mySeq = ++seqRef.current;

    const { and, validation } = buildAndFilters();
    console.log("validation", validation);
    if (!validation) return;

    const params = {
      $limit: 100,
      $skip: 0,
      ...(and.length ? { $and: and } : {}),
      ...((model?.searchFields || []).length
        ? {
            $or: (model.searchFields || []).map((sf) => ({
              [sf.id]: { $like: `%${searchText || ""}%` },
            })),
          }
        : {}),
    };

    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3030";
    const url = `${baseURL}/${field?.relation?.vc_nameTable}?dataString=${encodeURIComponent(
      JSON.stringify(params),
    )}`;
    console.log("service", "sigur");

    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: ctrl.signal,
      });

      if (!res.ok) {
        setOptions([]);
        return;
      }

      const response = await res.json();
      if (mySeq !== seqRef.current) return;

      let dataSorted = response?.data || [];
      if (model?.sort)
        dataSorted.sort(
          (a, b) => (a?.[model.sort] || 0) - (b?.[model.sort] || 0),
        );

      setOptions(dataSorted);

      // ✅ importante: si ya tienes value cargado y ahora llegaron opciones,
      // asegúrate de que value sea una de las options (misma referencia) para que MUI no marque inválido
      setValue((prev) => {
        if (!prev) return prev;
        const prevKey = getOptionKey(prev);
        const match = dataSorted.find((o) => getOptionKey(o) === prevKey);
        return match || prev; // si no existe, se queda pero ya no fallará por isOptionEqualToValue
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      setOptions([]);
    }
  };

  const searchData = async (searchText) => {
    console.log(isStatic);
    if (isStatic) searchLocal(searchText);
    else await searchRemote(searchText);
  };

  // defaultValue loader
  React.useEffect(() => {
    (async () => {
      if (!defaultValue) return;

      if (isStatic) {
        const found = (field?.options || []).find(
          (opt) => String(opt.value) === String(defaultValue),
        );
        if (found) setValue(found);
      } else {
        try {
          if (!service) return;
          const respon = await service.get(defaultValue, {});
          if (respon) setValue(respon);
        } catch (e) {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, isStatic, service]);

  // open behavior
  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }
    delaySearch(() => {
      searchData(inputValue || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // input behavior
  React.useEffect(() => {
    if (!open) return;
    console.log("input", inputValue);
    delaySearch(() => {
      searchData(inputValue || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  return [
    <Box
      key={`${id}-box`}
      display={"flex"}
      flexDirection={"row"}
      position={"relative"}
      width={"100%"}
    >
      <Box
        key={"title"}
        paddingTop={2}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          paddingBottom: 0,
          paddingTop: 20,
        }}
      >
        <div
          style={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 30,
            boxShadow: focus
              ? `0 0 0 2px ${theme.palette.primary.main}`
              : "0px 0px 2px gray",
            height: "100%",
            width: "100%",
          }}
        />
      </Box>

      <div key={"autocomplete"} style={{ flexGrow: 1, flexShrink: 1 }}>
        <Autocomplete
          key={id + "selector"}
          fullWidth
          disabled={isReadOnly}
          noOptionsText={"No hay coincidencias"}
          value={value || null}
          // ✅ FIX 1: comparación estable por llave (id / keyTable / value)
          isOptionEqualToValue={(option, val) => {
            return getOptionKey(option) === getOptionKey(val);
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(event, newValue) => setValue(newValue)}
          getOptionLabel={(option) => getLabel(option)}
          inputValue={inputValue || ""}
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          id={`${id}-autocomplete`}
          options={options}
          filterOptions={(e) => e}
          readOnly={isReadOnly}
          // ✅ FIX 2: key único para evitar "duplicate key"
          renderOption={(propsOption, option, { index }) => {
            const { key, ...rest } = propsOption;
            const uniqueKey = `${getOptionKey(option) || "opt"}-${index}`;
            return (
              <ListItem key={uniqueKey} {...rest}>
                {!isStatic && option?.icon && (
                  <IconMask
                    color={
                      getOptionKey(value) === getOptionKey(option)
                        ? theme.palette.primary.main
                        : theme.palette.mode === "dark"
                          ? "white"
                          : "black"
                    }
                    url={option.icon}
                    size={25}
                  />
                )}
                <Typography>{getLabel(option)}</Typography>
              </ListItem>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onFocus={(e) => {
                console.log("focus", e);
                setFocus(true);
              }}
              onBlur={(e) => {
                setFocus(false);
              }}
              onend
              label={`${field.label}${field.required ? "*" : ""}`}
              InputLabelProps={{
                sx: {
                  color: "gray",
                  padding: "18px 20px",
                  fontSize: 20,
                  [`&.${inputLabelClasses.shrink}`]: { padding: "0px 28px" },
                },
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                placeholder: field.label,
                style: {
                  padding: "12px 20px",
                  backgroundColor: "rgba(200,200,200,0)",
                  borderRadius: 10,
                  fontSize: 20,
                },
              }}
              sx={{
                paddingTop: 0.5,
              }}
              disabled={isReadOnly}
              fullWidth
              variant={variant}
            />
          )}
        />
      </div>

      <div key={"button"} style={{ flexGrow: 0, flexShrink: 1, width: "auto" }}>
        <Box paddingTop={4} paddingRight={1}>
          {!isStatic && !isReadOnly && (isAdmin || isSystem) && (
            <IconButton size="small">
              <CatalogsIconButton
                model={model}
                sizeButton={"small"}
                item={value || undefined}
                backgroundColor={"transparent"}
                size={20}
                color={theme.palette.mode === "light" ? "black" : "white"}
                icon={
                  !!value ? "assets/icons/edit.svg" : "assets/icons/add.svg"
                }
              />
            </IconButton>
          )}
        </Box>
      </div>

      <input hidden readOnly key={id} id={id} value={getHiddenValue()} />
    </Box>,
  ];
}
